import { useCallback, useEffect, useRef, useState } from "react";
import { companyProfile } from "../../../services/clientCalls";
import { errorHandler } from "../../../utils";
import {
  initialAccess,
  initialBasicInfo,
} from "../../../components/UI/Client/MyProfile/data";
import Breadcrumb from "../../../components/UI/Breadcrumb/Breadcrumb";
import ProfileHero from "../../../components/UI/Client/MyProfile/ProfileHero";
import ProfileStatsBar from "../../../components/UI/Client/MyProfile/ProfileStatsBar";
import TabBar from "../../../components/UI/Client/MyProfile/TabBar";
import PersonalInfoTab from "../../../components/UI/Client/MyProfile/PersonalInfoTab";
import AccessRoleTab from "../../../components/UI/Client/MyProfile/AccessRoleTab";
import NotificationsTab from "../../../components/UI/Client/MyProfile/NotificationsTab";
import { Toast } from "../../../components/Toast/Toast";
import { getNameInitials } from "../../../utils/functions";
import SecurityTab from "../../../components/UI/Client/MyProfile/SecurityTab";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";

const ClientProfile = () => {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState({ jobsPosted: 0, candidates: 0, hired: 0 });
  const [load, setLoad] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [access, setAccess] = useState(initialAccess);
  const [toast, setToast] = useState({ message: "", visible: false });
  const toastTimer = useRef(null);
  const getCompanyProfile = useCallback(async () => {
    try {
      setLoad(true);
      const res = await companyProfile();
      setData(res?.data?.data?.client);
      const nextStats = res?.data?.data?.stats;
      if (nextStats) {
        setStats({
          jobsPosted: nextStats.jobsPosted ?? 0,
          candidates: nextStats.candidates ?? 0,
          hired: nextStats.hired ?? 0,
        });
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => {
    getCompanyProfile();
  }, []);

  function showToast(message) {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, visible: true });
    toastTimer.current = setTimeout(() => {
      setToast((t) => ({ ...t, visible: false }));
    }, 3000);
  }

  const initials = getNameInitials(data?.firstName, data?.lastName);
  const fullName =
    data?.firstName && data?.lastName
      ? `${data?.firstName} ${data?.lastName}`
      : " ";

  return (
    <>
      {load && <FullScreenLoader />}
      <Breadcrumb
        crumbs={[
          { label: "Dashboard", href: "/client-activity-summary" },
          { label: "My Profile" },
        ]}
        title="My Profile"
      />

      <ProfileHero
        initials={initials}
        name={fullName}
        email={data?.email}
        phone={data?.phone}
        location={data?.companyAddress}
        isVerified={data?.profileVerified}
        role={data?.role}
        position={access.position}
      />

      <ProfileStatsBar stats={stats} />

      <TabBar activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "personal" && (
        <PersonalInfoTab
          basicInfo={data}
          onShowToast={showToast}
          getCompanyProfile={getCompanyProfile}
        />
      )}
      {activeTab === "access" && (
        <AccessRoleTab
          data={data}
          onShowToast={showToast}
          getCompanyProfile={getCompanyProfile}
        />
      )}
      {activeTab === "security" && <SecurityTab onShowToast={showToast} />}
      {activeTab === "notifications" && <NotificationsTab />}

      <Toast message={toast.message} visible={toast.visible} />
    </>
  );
};

export default ClientProfile;
